import { useState, useRef, useEffect, useCallback } from "react";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";


const formatDisplay = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatNumber = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(2) + "K";
  return n.toFixed(2);
};


const StatCard = ({ icon, label, value, sub, loading }) => (
  <div className="dash-stat-card">
    <div className="dash-stat-header">
      <span className="dash-stat-icon">{icon}</span>
      <span className="dash-stat-label">{label}</span>
    </div>
    <div className="dash-stat-value">{loading ? "—" : value}</div>
    <div className="dash-stat-sub">{sub}</div>
  </div>
);

{
  /* Icons */
}

const LightningIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13 2 3 14h7l-1 8 10-12h-7z" />
  </svg>
);

const TrendDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 18l-6-6-4 4-7-7" />
    <path d="M17 12h6v6" />
  </svg>
);

// ── Main Component ─────────────────────────────────────────────────────────
const EnergyAnalysisPage = () => {
  const {
    reportingYear,
    setReportingYear,
    reportingYearId,
  } = useReportingYear();

 
  const [report, setReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportingYearInfo, setReportingYearInfo] =useState(null);
  const [legalEntities, setLegalEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");

 

  // ── Legal entity API ────────────────────────────────────────────────────────────────
useEffect(() => {

   const loadReportingYear = async () => {

    try {

        const res =
            await apiClient.post(
                "/api/reportingyear"
            );

        if (res.data.code === 200) {

            const selected =
                (res.data.data || [])
                    .find(
                        y =>
                            Number(y.id) ===
                            Number(reportingYearId)
                    );

            setReportingYearInfo(
                selected || null
            );
        }

    } catch (e) {

        console.error(
            "Reporting year API error:",
            e
        );
    }
};

    loadReportingYear();

}, [reportingYearId]);
  useEffect(() => {
    const fetchLegalEntities = async () => {
      if (!reportingYearId) return;

      try {
        const res = await apiClient.get(`/api/legal-entities-by-year`, {
          params: {
            reporting_year_id: reportingYearId,
          },
        });

        if (res.data.code === 200) {
          setLegalEntities(res.data.data || []);
        } else {
          setLegalEntities([]);
        }
      } catch (err) {
        console.error("Legal entities API error:", err);
        setLegalEntities([]);
      }
    };

    fetchLegalEntities();
  }, [reportingYearId]);

  

  // ── Fetch ────────────────────────────────────────────────────────────────
  // =====================================
// LOAD
// =====================================

const loadReport = async () => {

    if (
        !reportingYearId ||
        !selectedEntity
    ) {
        return;
    }

    try {

        setLoading(true);
        setError(null);

        const response =
            await apiClient.get(
                "/api/reports/energy-analysis",
                {
                    params: {

                        reporting_year_id:
                            reportingYearId,

                        legal_entity_id:
                            selectedEntity,

                        ...filters
                    }
                }
            );

        setReport(response.data);

        if (response.data?.success) {

            setData(response.data);

        } else {

            setData(null);

            setError(
                "Failed to load energy data."
            );
        }

    } catch (err) {

        console.error(err);

        setData(null);

        setError(
            "Failed to load energy data."
        );

    } finally {

        setLoading(false);
    }
};

useEffect(() => {

    loadReport();

}, [
    reportingYearId,
    selectedEntity
]);

// =====================================
    // FILTER
    // =====================================

    const [filters, setFilters] =
        useState({
            filter_type: "THIS_YEAR",
            quarter: "",
            month: "",
            from_date: "",
            to_date: ""
        });

      const handleFilterChange = (field, value) => {

    const updated = {
        ...filters,
        [field]: value
    };

    if(field === "filter_type") {

        updated.quarter = "";
        updated.month = "";

        if(value === "CUSTOM" && reportingYearInfo) {
            updated.from_date =
                reportingYearInfo.start_date;

            updated.to_date =
                reportingYearInfo.end_date;
        }

        if(value !== "CUSTOM") {
            updated.from_date = "";
            updated.to_date = "";
        }
    }

    setFilters(updated);
};

  // ── Derived ──────────────────────────────────────────────────────────────
  const summary = data?.summary || {};
  const distribution = data?.distribution || [];
  const monthly = data?.monthly || [];
  const sitewise = data?.sitewise || [];
  const features = data?.features || {};

  

  // ✅ Y-axis scaling logic
  const maxValue = Math.max(
    0,
    ...monthly.map((m) => Number(m.fuel_kWhEq || 0)),
    ...monthly.map((m) => Number(m.electricity_kWh || 0))
  );

  const step = 1000;

  const ticks = [];
  for (let i = 0; i <= maxValue + step; i += step) {
    ticks.push(i);
  }

  return (
    <div className="dash-page">
      <div className="dash-page-title">Energy Consumption Analysis</div>
      <div className="dash-page-subtitle">
        Energy Consumption, Source Mix and Operational Efficiency Analysis
      </div>
     

      <div className="filter-card">
        

        <div className="filter-group">
          <label>Legal Entity *</label>

          <select
            className="filter-fields"
            value={selectedEntity}
            onChange={(e) => {
              setSelectedEntity(e.target.value);
              setLoading(true);
            }}
          >
            <option value="">Select</option>

            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">

                    <label>Period</label>

                    <select
                        className="filter-fields"
                        value={filters.filter_type}
                        onChange={e => handleFilterChange("filter_type", e.target.value)}
                    >
                        <option value="THIS_YEAR">This Year</option>
                        <option value="THIS_QUARTER">Quarter</option>
                        <option value="THIS_MONTH">Month</option>
                        <option value="CUSTOM">Custom</option>
                    </select>

                </div>
                {
                    filters.filter_type === "THIS_QUARTER" && (
        <div className="filter-group">

            <label>Quarter</label>

            <select
                className="filter-fields"
                value={filters.quarter}
                onChange={e => handleFilterChange("quarter", e.target.value)}
            >
                <option value="">Select Quarter</option>
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
            </select>

        </div>
    )
}
{
    filters.filter_type === "THIS_MONTH" && (
        <div className="filter-group">

            <label>Month</label>

            <select
                className="filter-fields"
                value={filters.month}
                onChange={e => handleFilterChange("month", e.target.value)}
            >

                <option value="">Select Month</option>

                {
                    (() => {

                        const months = [];

                        const start = new Date(
                            report?.reporting_period?.from_date ||
                            `${new Date().getFullYear()}-04-01`
                        );

                        const current = new Date();
                        const temp = new Date(start);

                        while(
                            temp.getFullYear() < current.getFullYear() ||
                            (
                                temp.getFullYear() === current.getFullYear() &&
                                temp.getMonth() <= current.getMonth()
                            )
                        ) {

                            const year = temp.getFullYear();

                            const month = String(
                                temp.getMonth() + 1
                            ).padStart(2, "0");

                            months.push({
                                value: `${year}-${month}`,
                                label: temp.toLocaleString(
                                    "en-IN",
                                    {
                                        month: "short",
                                        year: "numeric"
                                    }
                                )
                            });

                            temp.setMonth(
                                temp.getMonth() + 1
                            );
                        }

                        return months.map(item => (
                            <option
                                key={item.value}
                                value={item.value}
                            >
                                {item.label}
                            </option>
                        ));
                    })()
                }

            </select>

        </div>
    )
}

{
    filters.filter_type === "CUSTOM" && (
        <>
            <div className="filter-group">

                <label>
                    From
                </label>

                <input
                    type="date"
                    className="filter-fields"
                    min={
                        reportingYearInfo?.start_date
                    }
                    max={
                        reportingYearInfo?.end_date
                    }
                    value={filters.from_date}
                    onChange={(e) =>
                        handleFilterChange(
                            "from_date",
                            e.target.value
                        )
                    }
        />

            </div>

            <div className="filter-group">

                <label>
                    To
                </label>

                <input
                        type="date"
                        className="filter-fields"
                        min={
                            reportingYearInfo?.start_date
                        }
                        max={
                            reportingYearInfo?.end_date
                        }
                        value={filters.to_date}
                        onChange={(e) =>
                            handleFilterChange(
                                "to_date",
                                e.target.value
                            )
                        }
                    />

            </div>
        </>
    )
}
<div className="filter-group">

                    <label>&nbsp;</label>

                    <button className="org-btn-save" onClick={loadReport}>
                        Generate Report
                    </button>

                </div>
      </div>

        {/* EMPTY STATE */}
      {!selectedEntity && (
        <div className="st-empty-entity">
          <div className="st-empty-entity__icon">
            <i className="fa-solid fa-building" />
          </div>

          <div className="st-empty-entity__title">
            Select a Legal Entity to continue
          </div>

          <div className="st-empty-entity__sub">
            Select a legal entity above to view Energy Consumption Analysis,
            emission trends, Energy Consumption summaries, and analytics reports.
          </div>
        </div>
      )}

      {
    report && (() => {

        const {
            applied_filter,
            summary,
            distribution,
            monthly,
            sitewise,
            features
        } = report;

        return (

            <>
                <div className="reporting_year_line">
                    <strong>
                        {applied_filter.label}
                    </strong>
                    {" | "}
                    {applied_filter.from_date}
                    {" to "}
                    {applied_filter.to_date}
                </div>
      {/* ── Summary Stats ── */}
      <div className="dash-stat-grid" style={{ marginTop: 20 }}>
        <StatCard
            icon={<LightningIcon />}
            label="TOTAL ENERGY"
            value={
                formatNumber(
                    summary.total_energy_mwh
                )
            }
            sub="MWh"
            loading={loading}
        />
        <StatCard
            icon="🏭"
            label="TOP LOCATION"
            value={
                summary.top_site
            }
            sub="Highest consumption"
            loading={loading}
        />
        <StatCard
          icon={<LightningIcon />}
          label="ELECTRICITY"
          value={formatNumber(summary.Electricity_kwh)}
          sub="kWh"
          loading={loading}
        />
        <StatCard
          icon={<LightningIcon />}
          label="FUEL ENERGY"
          value={formatNumber(summary.Fuel_kWhEq)}
          sub="kWh equivalent"
          loading={loading}
        />
        <StatCard
            icon="⚡"
            label="TOP ENERGY"
            value={
                summary.top_energy_source
            }
            sub="Highest consumption"
            loading={loading}
        />
        <StatCard
          icon="📈"
          label="DATA POINTS"
          value={summary.data_points ?? 0}
          sub="Entries in range"
          loading={loading}
        />
      </div>

      {/* ── Charts Row (Donut + Line) ── */}
      {!loading && distribution.length > 0 && monthly.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginTop: 20,
          }}
        >
          {/* 🔹 Donut Chart */}
          <div className="dash-card">
            <div className="dash-section-title">Energy Mix by Type</div>

            <ResponsiveContainer width="100%" height="100%">
              <PieChart className="chartSize">
                <Tooltip />

                <Pie
                  data={distribution.map((d) => ({
                    name: d.EnergyType,
                    value: Number(d.Consumption_kWh),
                  }))}
                  dataKey="value"
                  cx="45%"
                  cy="45%"
                  outerRadius={100}
                  innerRadius={48}
                  labelLine={true}
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  {distribution.map((_, index) => (
                    <Cell
                      key={index}
                      fill={["#22c55e", "#3b82f6", "#f59e0b"][index % 3]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 🔹 Line Chart */}
          <div className="dash-card">
            <div className="dash-section-title">Monthly Energy Trend</div>
            <br></br>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart className="chartSize" data={monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="Month" />
                <YAxis />
                <Tooltip />
                <Legend />

                <Line
                  type="monotone"
                  dataKey="Electricity_kWh"
                  stroke="#3b82f6"
                />
                <Line type="monotone" dataKey="Fuel_kWhEq" stroke="#f59e0b" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Site-wise Chart ── */}
      {!loading && sitewise.length > 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-section-title">Site-wise Energy Comparison</div>
          <br></br>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart className="chartSize" data={sitewise}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="site_name" />
              <YAxis />
              <Tooltip />
              <Legend />

              <Bar dataKey="fuel_energy_kwh" fill="#f59e0b" />
              <Bar dataKey="electricity_kwh" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {error && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-empty" style={{ color: "#e53e3e" }}>
            {error}
          </div>
        </div>
      )}

      {!loading && !error && distribution.length === 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-empty">
            No energy or emission entries found for the selected date range.
          </div>
        </div>
      )}

      {/* ── Energy Distribution ── */}
      {!loading && distribution.length > 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-section-title">Energy Distribution by Type</div>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Energy Type</th>
                <th>Consumption (kWh)</th>
                <th>Data Points</th>
                <th>% of Total</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {distribution.map((d) => (
                <tr key={d.EnergyType}>
                  <td data-label="Energy Type">{d.EnergyType}</td>
                  <td data-label="Consumption">
                    {formatNumber(d.Consumption_kWh)}
                  </td>
                  <td data-label="Data Points">{d.DataPoints}</td>
                  <td data-label="%">
                    {parseFloat(d.PercentOfTotal).toFixed(2)}%
                  </td>

                  <td data-label="Share">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${parseFloat(d.PercentOfTotal)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Monthly Trend ── */}
      {!loading && monthly.length > 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-section-title">Monthly Trend</div>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Electricity (kWh)</th>
                <th>Fuel (kWh eq.)</th>
                <th>Total (kWh)</th>
                <th>Data Points</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m) => (
                <tr key={m.Month}>
                  <td data-label="Month">{m.Month}</td>
                  <td data-label="Electricity">
                    {formatNumber(m.Electricity_kWh)}
                  </td>
                  <td data-label="Fuel">{formatNumber(m.Fuel_kWhEq)}</td>
                  <td data-label="Total">
                    <b>{formatNumber(m.Total_kWh)}</b>
                  </td>
                  <td data-label="Data Points">{m.DataPoints}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Site-wise Breakdown ── */}
      {!loading && sitewise.length > 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-section-title">Site-wise Breakdown</div>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Total Energy (kWh)</th>
                <th>Electricity (kWh)</th>
                <th>Fuel Energy (kWh)</th>
                <th>Contribution %</th>
              </tr>
            </thead>
            <tbody>
              {sitewise.map((s) => (
                <tr key={s.site_name}>
                  <td data-label="Site">{s.site_name}</td>
                  <td data-label="Total Energy">
                    <b>{formatNumber(s.total_energy_kwh)}</b>
                  </td>
                  <td data-label="Electricity">
                    {formatNumber(s.electricity_kwh)}
                  </td>
                  <td data-label="Fuel">{formatNumber(s.fuel_energy_kwh)}</td>
                  <td>{s.contribution_percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      )}
</>   // <-- Missing fragment closing tag
       );

    })()
}

      {/* ── Loading overlay ── */}
      {loading && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-empty">Loading energy data…</div>
        </div>
      )}
    </div>
  );
};

export default EnergyAnalysisPage;
